/** Autonomous communities of the mirrored official registries, for the
 * statistics filters. KEEP IN SYNC with the sources in official-sync.ts. */
export type CommunityInfo = {
  id: string;
  name: string;
  cityIds: readonly string[];
};

export const COMMUNITIES: readonly CommunityInfo[] = [
  {
    id: 'andalucia',
    name: 'Andalucía',
    cityIds: [
      'sevilla',
      'malaga',
      'granada',
      'cordoba',
      'cadiz',
      'huelva',
      'jaen',
      'almeria',
      'jerez-de-la-frontera',
      'marbella',
    ],
  },
  { id: 'cataluna', name: 'Cataluña', cityIds: ['barcelona', 'girona', 'tarragona'] },
  {
    id: 'comunitat-valenciana',
    name: 'Comunidad Valenciana',
    cityIds: ['valencia', 'alicante', 'benidorm', 'torrevieja', 'calp', 'denia'],
  },
  {
    id: 'illes-balears',
    name: 'Islas Baleares',
    cityIds: [
      'palma',
      'calvia',
      'alcudia',
      'ciutadella',
      'mao',
      'sant-lluis',
      'es-mercadal',
      'alaior',
      'eivissa',
      'sant-josep-de-sa-talaia',
      'sant-antoni-de-portmany',
      'santa-eularia-des-riu',
      'sant-joan-de-labritja',
    ],
  },
  { id: 'navarra', name: 'Navarra', cityIds: ['pamplona'] },
  { id: 'euskadi', name: 'Euskadi', cityIds: ['donostia', 'bilbao'] },
  { id: 'comunidad-de-madrid', name: 'Comunidad de Madrid', cityIds: ['madrid'] },
  {
    id: 'region-de-murcia',
    name: 'Región de Murcia',
    cityIds: [
      'cartagena',
      'san-javier',
      'torre-pacheco',
      'murcia',
      'mazarron',
      'los-alcazares',
      'san-pedro-del-pinatar',
      'aguilas',
    ],
  },
  {
    id: 'galicia',
    name: 'Galicia',
    cityIds: ['vigo', 'a-coruna', 'santiago-de-compostela', 'sanxenxo', 'o-grove'],
  },
  {
    id: 'castilla-y-leon',
    name: 'Castilla y León',
    cityIds: ['leon', 'burgos', 'salamanca', 'valladolid', 'zamora', 'avila', 'soria'],
  },
  {
    id: 'aragon',
    name: 'Aragón',
    cityIds: ['zaragoza', 'jaca', 'benasque', 'sallent-de-gallego', 'panticosa', 'teruel'],
  },
  {
    id: 'castilla-la-mancha',
    name: 'Castilla-La Mancha',
    cityIds: ['toledo', 'cuenca', 'albacete'],
  },
  { id: 'extremadura', name: 'Extremadura', cityIds: ['caceres', 'merida', 'badajoz'] },
  {
    id: 'canarias',
    name: 'Canarias',
    cityIds: [
      'las-palmas-de-gran-canaria',
      'santa-cruz-de-tenerife',
      'arona',
      'adeje',
      'san-bartolome-de-tirajana',
      'mogan',
      'la-oliva',
      'yaiza',
      'tias',
    ],
  },
  {
    id: 'cantabria',
    name: 'Cantabria',
    cityIds: [
      'suances',
      'san-vicente-de-la-barquera',
      'miengo',
      'pielagos',
      'laredo',
      'comillas',
      'santander',
      'ribamontan-al-mar',
      'noja',
      'santillana-del-mar',
      'castro-urdiales',
      'torrelavega',
    ],
  },
  { id: 'la-rioja', name: 'La Rioja', cityIds: ['logrono', 'haro', 'ezcaray'] },
  { id: 'asturias', name: 'Asturias', cityIds: ['gijon', 'aviles'] },
];

const CITY_NAMES: Readonly<Record<string, string>> = {
  sevilla: 'Sevilla',
  malaga: 'Málaga',
  granada: 'Granada',
  cordoba: 'Córdoba',
  cadiz: 'Cádiz',
  huelva: 'Huelva',
  jaen: 'Jaén',
  almeria: 'Almería',
  'jerez-de-la-frontera': 'Jerez de la Frontera',
  marbella: 'Marbella',
  barcelona: 'Barcelona',
  valencia: 'Valencia',
  alicante: 'Alicante',
  benidorm: 'Benidorm',
  palma: 'Palma',
  pamplona: 'Pamplona',
  donostia: 'San Sebastián',
  bilbao: 'Bilbao',
  madrid: 'Madrid',
  torrevieja: 'Torrevieja',
  calp: 'Calpe',
  denia: 'Denia',
  calvia: 'Calvià',
  alcudia: 'Alcudia',
  girona: 'Girona',
  tarragona: 'Tarragona',
  'las-palmas-de-gran-canaria': 'Las Palmas de Gran Canaria',
  'santa-cruz-de-tenerife': 'Santa Cruz de Tenerife',
  arona: 'Arona',
  adeje: 'Adeje',
  'san-bartolome-de-tirajana': 'San Bartolomé de Tirajana',
  mogan: 'Mogán',
  'la-oliva': 'La Oliva',
  yaiza: 'Yaiza',
  tias: 'Tías',
  cartagena: 'Cartagena',
  'san-javier': 'San Javier',
  'torre-pacheco': 'Torre Pacheco',
  murcia: 'Murcia',
  mazarron: 'Mazarrón',
  'los-alcazares': 'Los Alcázares',
  'san-pedro-del-pinatar': 'San Pedro del Pinatar',
  aguilas: 'Águilas',
  ciutadella: 'Ciudadela',
  mao: 'Mahón',
  'sant-lluis': 'San Luis',
  'es-mercadal': 'Es Mercadal',
  alaior: 'Alayor',
  vigo: 'Vigo',
  'a-coruna': 'La Coruña',
  'santiago-de-compostela': 'Santiago de Compostela',
  sanxenxo: 'Sangenjo',
  'o-grove': 'El Grove',
  leon: 'León',
  burgos: 'Burgos',
  salamanca: 'Salamanca',
  valladolid: 'Valladolid',
  zamora: 'Zamora',
  avila: 'Ávila',
  soria: 'Soria',
  zaragoza: 'Zaragoza',
  jaca: 'Jaca',
  benasque: 'Benasque',
  'sallent-de-gallego': 'Sallent de Gállego',
  panticosa: 'Panticosa',
  teruel: 'Teruel',
  toledo: 'Toledo',
  cuenca: 'Cuenca',
  albacete: 'Albacete',
  caceres: 'Cáceres',
  merida: 'Mérida',
  badajoz: 'Badajoz',
  eivissa: 'Ibiza',
  'sant-josep-de-sa-talaia': 'San José',
  'sant-antoni-de-portmany': 'San Antonio de Portmany',
  'santa-eularia-des-riu': 'Santa Eulalia del Río',
  'sant-joan-de-labritja': 'San Juan de Labritja',
  suances: 'Suances',
  'san-vicente-de-la-barquera': 'San Vicente de la Barquera',
  miengo: 'Miengo',
  pielagos: 'Piélagos',
  laredo: 'Laredo',
  comillas: 'Comillas',
  santander: 'Santander',
  'ribamontan-al-mar': 'Ribamontán al Mar',
  noja: 'Noja',
  'santillana-del-mar': 'Santillana del Mar',
  'castro-urdiales': 'Castro-Urdiales',
  torrelavega: 'Torrelavega',
  logrono: 'Logroño',
  haro: 'Haro',
  ezcaray: 'Ezcaray',
  gijon: 'Gijón',
  aviles: 'Avilés',
};

export function cityDisplayName(cityId: string): string {
  return CITY_NAMES[cityId] ?? cityId;
}

/** Center + plausible municipal radius per covered city — lets the map know
 * it is over a covered city without any geocoding call. KEEP IN SYNC with
 * MUNICIPALITY_CENTERS in functions/src/domain/openrta.ts. */
export const CITY_CENTERS: Readonly<
  Record<string, { lat: number; lng: number; radiusKm: number }>
> = {
  sevilla: { lat: 37.3891, lng: -5.9845, radiusKm: 30 },
  malaga: { lat: 36.7213, lng: -4.4214, radiusKm: 30 },
  granada: { lat: 37.1773, lng: -3.5986, radiusKm: 25 },
  cordoba: { lat: 37.8882, lng: -4.7794, radiusKm: 45 },
  cadiz: { lat: 36.5297, lng: -6.2927, radiusKm: 20 },
  huelva: { lat: 37.2614, lng: -6.9447, radiusKm: 25 },
  jaen: { lat: 37.7796, lng: -3.7849, radiusKm: 30 },
  almeria: { lat: 36.834, lng: -2.4637, radiusKm: 35 },
  'jerez-de-la-frontera': { lat: 36.6866, lng: -6.1372, radiusKm: 45 },
  marbella: { lat: 36.5101, lng: -4.8825, radiusKm: 30 },
  barcelona: { lat: 41.3874, lng: 2.1686, radiusKm: 20 },
  valencia: { lat: 39.4699, lng: -0.3763, radiusKm: 22 },
  alicante: { lat: 38.3452, lng: -0.481, radiusKm: 22 },
  benidorm: { lat: 38.5382, lng: -0.131, radiusKm: 12 },
  palma: { lat: 39.5696, lng: 2.6502, radiusKm: 22 },
  pamplona: { lat: 42.8125, lng: -1.644, radiusKm: 12 },
  donostia: { lat: 43.3183, lng: -1.9812, radiusKm: 12 },
  bilbao: { lat: 43.263, lng: -2.935, radiusKm: 12 },
  madrid: { lat: 40.4168, lng: -3.7038, radiusKm: 25 },
  torrevieja: { lat: 37.9787, lng: -0.6822, radiusKm: 15 },
  calp: { lat: 38.6446, lng: 0.0453, radiusKm: 10 },
  denia: { lat: 38.8408, lng: 0.1057, radiusKm: 15 },
  calvia: { lat: 39.5657, lng: 2.5062, radiusKm: 18 },
  alcudia: { lat: 39.8499, lng: 3.124, radiusKm: 12 },
  girona: { lat: 41.9794, lng: 2.8214, radiusKm: 12 },
  tarragona: { lat: 41.1189, lng: 1.2445, radiusKm: 15 },
  'las-palmas-de-gran-canaria': { lat: 28.1235, lng: -15.4363, radiusKm: 16 },
  'santa-cruz-de-tenerife': { lat: 28.4636, lng: -16.2518, radiusKm: 18 },
  arona: { lat: 28.0996, lng: -16.681, radiusKm: 14 },
  adeje: { lat: 28.117, lng: -16.726, radiusKm: 16 },
  'san-bartolome-de-tirajana': { lat: 27.85, lng: -15.58, radiusKm: 24 },
  mogan: { lat: 27.8848, lng: -15.7145, radiusKm: 18 },
  'la-oliva': { lat: 28.611, lng: -13.927, radiusKm: 20 },
  yaiza: { lat: 28.91, lng: -13.8, radiusKm: 16 },
  tias: { lat: 28.94, lng: -13.65, radiusKm: 11 },
  cartagena: { lat: 37.6257, lng: -0.9966, radiusKm: 30 },
  'san-javier': { lat: 37.8063, lng: -0.8371, radiusKm: 16 },
  'torre-pacheco': { lat: 37.7434, lng: -0.9548, radiusKm: 12 },
  murcia: { lat: 37.9922, lng: -1.1307, radiusKm: 30 },
  mazarron: { lat: 37.599, lng: -1.313, radiusKm: 18 },
  'los-alcazares': { lat: 37.7442, lng: -0.8497, radiusKm: 8 },
  'san-pedro-del-pinatar': { lat: 37.8362, lng: -0.7917, radiusKm: 8 },
  aguilas: { lat: 37.4063, lng: -1.5828, radiusKm: 18 },
  ciutadella: { lat: 40.0022, lng: 3.8378, radiusKm: 15 },
  mao: { lat: 39.8885, lng: 4.2658, radiusKm: 12 },
  'sant-lluis': { lat: 39.849, lng: 4.2586, radiusKm: 10 },
  'es-mercadal': { lat: 39.9905, lng: 4.0946, radiusKm: 15 },
  alaior: { lat: 39.9333, lng: 4.14, radiusKm: 14 },
  vigo: { lat: 42.2406, lng: -8.7207, radiusKm: 18 },
  'a-coruna': { lat: 43.3623, lng: -8.4115, radiusKm: 12 },
  'santiago-de-compostela': { lat: 42.8782, lng: -8.5449, radiusKm: 15 },
  sanxenxo: { lat: 42.3999, lng: -8.8069, radiusKm: 10 },
  'o-grove': { lat: 42.4934, lng: -8.8642, radiusKm: 8 },
  leon: { lat: 42.5987, lng: -5.5671, radiusKm: 12 },
  burgos: { lat: 42.3439, lng: -3.6969, radiusKm: 15 },
  salamanca: { lat: 40.9701, lng: -5.6635, radiusKm: 12 },
  valladolid: { lat: 41.6523, lng: -4.7245, radiusKm: 15 },
  zamora: { lat: 41.5036, lng: -5.7448, radiusKm: 12 },
  avila: { lat: 40.6565, lng: -4.6818, radiusKm: 15 },
  soria: { lat: 41.7636, lng: -2.4649, radiusKm: 12 },
  zaragoza: { lat: 41.6488, lng: -0.8891, radiusKm: 30 },
  jaca: { lat: 42.5701, lng: -0.5498, radiusKm: 20 },
  benasque: { lat: 42.6062, lng: 0.5232, radiusKm: 12 },
  'sallent-de-gallego': { lat: 42.7702, lng: -0.3327, radiusKm: 12 },
  panticosa: { lat: 42.7223, lng: -0.2831, radiusKm: 10 },
  teruel: { lat: 40.3457, lng: -1.1065, radiusKm: 18 },
  toledo: { lat: 39.8628, lng: -4.0273, radiusKm: 15 },
  cuenca: { lat: 40.0704, lng: -2.1374, radiusKm: 25 },
  albacete: { lat: 38.9943, lng: -1.8585, radiusKm: 20 },
  caceres: { lat: 39.4753, lng: -6.3724, radiusKm: 25 },
  merida: { lat: 38.9161, lng: -6.3437, radiusKm: 15 },
  badajoz: { lat: 38.8794, lng: -6.9707, radiusKm: 20 },
  eivissa: { lat: 38.9089, lng: 1.4329, radiusKm: 8 },
  'sant-josep-de-sa-talaia': { lat: 38.9223, lng: 1.2936, radiusKm: 20 },
  'sant-antoni-de-portmany': { lat: 38.9807, lng: 1.3004, radiusKm: 14 },
  'santa-eularia-des-riu': { lat: 38.9846, lng: 1.5342, radiusKm: 18 },
  'sant-joan-de-labritja': { lat: 39.0777, lng: 1.5122, radiusKm: 16 },
  suances: { lat: 43.4262, lng: -4.0459, radiusKm: 8 },
  'san-vicente-de-la-barquera': { lat: 43.3852, lng: -4.3963, radiusKm: 10 },
  miengo: { lat: 43.4237, lng: -3.999, radiusKm: 8 },
  pielagos: { lat: 43.3963, lng: -3.954, radiusKm: 12 },
  laredo: { lat: 43.4147, lng: -3.4109, radiusKm: 8 },
  comillas: { lat: 43.3868, lng: -4.2909, radiusKm: 8 },
  santander: { lat: 43.4623, lng: -3.8099, radiusKm: 12 },
  'ribamontan-al-mar': { lat: 43.457, lng: -3.705, radiusKm: 10 },
  noja: { lat: 43.489, lng: -3.527, radiusKm: 6 },
  'santillana-del-mar': { lat: 43.3903, lng: -4.1077, radiusKm: 8 },
  'castro-urdiales': { lat: 43.3849, lng: -3.2159, radiusKm: 12 },
  torrelavega: { lat: 43.3494, lng: -4.0473, radiusKm: 8 },
  logrono: { lat: 42.4627, lng: -2.4449, radiusKm: 12 },
  haro: { lat: 42.576, lng: -2.8459, radiusKm: 8 },
  ezcaray: { lat: 42.3266, lng: -3.0114, radiusKm: 12 },
  gijon: { lat: 43.5322, lng: -5.6611, radiusKm: 15 },
  aviles: { lat: 43.556, lng: -5.9248, radiusKm: 8 },
};

/** Covered city under the map center at city-level zoom, or null. */
export function coveredCityForPosition(
  position: { lat: number; lng: number },
  zoom: number,
  distanceMeters: (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => number,
): string | null {
  if (zoom < 11) return null;
  let best: { id: string; distance: number } | null = null;
  for (const [id, center] of Object.entries(CITY_CENTERS)) {
    const distance = distanceMeters(position, { lat: center.lat, lng: center.lng });
    if (distance <= center.radiusKm * 1000 && (best === null || distance < best.distance)) {
      best = { id, distance };
    }
  }
  return best?.id ?? null;
}

export function communityForCity(cityId: string): CommunityInfo | null {
  return COMMUNITIES.find((community) => community.cityIds.includes(cityId)) ?? null;
}
